import { useState } from "react";
import { CreditCard, ShieldCheck } from "lucide-react";
import { useToast } from "../../components/ui/toast";
import { cn, formatINR } from "../../lib/utils";
import type { AutoRechargeRules } from "./types";

interface PaymentMethodsTabProps {
  rules: AutoRechargeRules;
  onSaveRules: (next: AutoRechargeRules) => void;
}

export function PaymentMethodsTab({ rules, onSaveRules }: PaymentMethodsTabProps) {
  const toast = useToast();
  const [draft, setDraft] = useState<AutoRechargeRules>(rules);
  const dirty = JSON.stringify(draft) !== JSON.stringify(rules);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {/* Card on file */}
      <div className="space-y-4">
        <div className="relative overflow-hidden rounded-2xl bg-ink p-6 text-white shadow-lg">
          <div className="flex items-start justify-between">
            <span className="h-8 w-11 rounded-md bg-gradient-to-br from-brand to-brand-hover" />
            <span className="font-mono text-sm font-bold italic">VISA</span>
          </div>
          <p className="mt-6 font-mono text-lg tracking-widest">•••• •••• •••• 4242</p>
          <div className="mt-4 flex justify-between font-mono text-xs text-white/70">
            <span>AARAV SHARMA</span>
            <span>12/26</span>
          </div>
        </div>

        <button
          onClick={() => toast.info("Opening secure payment provider…", "Card updates happen outside this app (PCI scope).")}
          className="flex items-center gap-2 rounded-lg border border-line px-4 py-2.5 text-sm font-semibold text-ink transition-colors hover:bg-soft"
        >
          <CreditCard className="h-4 w-4" /> Update Payment Method
        </button>
        <p className="flex items-center gap-1.5 text-xs text-ink-3">
          <ShieldCheck className="h-3.5 w-3.5 text-success" />
          Cards are tokenized by our payment provider. We never store card numbers.
        </p>
      </div>

      {/* Auto-recharge rules */}
      <div className="rounded-xl shadow-card dark:shadow-card-dark bg-paper-card dark:bg-[#292524] p-4 sm:p-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-ink">Auto-Recharge</h2>
            <p className="text-sm text-ink-2">Never let a batch die mid-campaign.</p>
          </div>
          <button
            role="switch"
            aria-checked={draft.enabled}
            onClick={() => setDraft((d) => ({ ...d, enabled: !d.enabled }))}
            className={cn("relative h-5 w-9 rounded-full transition-colors", draft.enabled ? "bg-brand" : "bg-line")}
          >
            <span className={cn("absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all", draft.enabled ? "left-[18px]" : "left-0.5")} />
          </button>
        </div>

        <div className={cn("mt-5 space-y-4 transition-opacity", !draft.enabled && "pointer-events-none opacity-40")}>
          <label className="block">
            <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">
              Recharge when wallet drops below
            </span>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-sm text-ink-3">₹</span>
              <input
                type="number" min={0} step={500} value={draft.threshold}
                onChange={(e) => setDraft((d) => ({ ...d, threshold: Number(e.target.value) }))}
                className="w-full rounded-lg border border-line bg-elevated py-2.5 pl-8 pr-3 font-mono text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
              />
            </div>
          </label>

          <label className="block">
            <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">
              Top-up amount
            </span>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-sm text-ink-3">₹</span>
              <input
                type="number" min={0} step={1000} value={draft.amount}
                onChange={(e) => setDraft((d) => ({ ...d, amount: Number(e.target.value) }))}
                className="w-full rounded-lg border border-line bg-elevated py-2.5 pl-8 pr-3 font-mono text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
              />
            </div>
          </label>

          <p className="rounded-lg bg-soft px-3 py-2 text-xs text-ink-2">
            When balance &lt; {formatINR(draft.threshold)}, we automatically add {formatINR(draft.amount)} to your wallet.
          </p>
        </div>

        <button
          disabled={!dirty || !draft.enabled}
          onClick={() => { onSaveRules(draft); toast.success("Auto-recharge rules updated"); }}
          className={cn(
            "mt-5 w-full rounded-lg py-2.5 text-sm font-semibold text-white transition-colors",
            dirty && draft.enabled ? "bg-brand hover:bg-brand-hover" : "cursor-not-allowed bg-line text-ink-3",
          )}
        >
          Save Rules
        </button>
      </div>
    </div>
  );
}
